import { getControllerFromRoute } from '@/helper/RouteHelper';
import type { VHHuongDanItem, VHHuongDanSuDungResponse } from '@/types/vanHoaHuongDan';

/** FE controller keys có icon `?` — chỉ trang có `btn tourguide` trong CMS VanHoa cũ. */
export const VH_HUONG_DAN_FE_CONTROLLERS = [
  //'vanhoa_dashboard',
  'vanhoa_timeline',
  'vanhoa_bangkehoach',
  'vanhoa_danhgia',
  'vanhoa_ngoinha',
  'vanhoa_hanhvithenchot',
  'vanhoa_anle',
  'vanhoa_cauhoianle',
  'vanhoa_hinhmau_tieuchi',
  'vanhoa_hinhmau_luachon',
  'vanhoa_hinhmau_danhgia',
  'vanhoa_tieuchigankettrainghiemdiemcham',
  'vanhoa_tieuchitruongthanhvanhoa',
  'vanhoa_dotkhaosat',
  'vanhoa_actionplan',
] as const;

/** Target mặc định khi mở modal (`txtyeutothanhcong`) — khớp toolbar CMS cũ. */
export const VH_HUONG_DAN_DEFAULT_TARGET: Record<string, string> = {
  dashboard: 'tongquanvanhoa',
  dashboardvanhoa: 'timeline',
  bangkehoach: 'bangkehoach',
  danhgia: 'danhgia',
  ngoinha: 'ngoinha',
  hanhvithenchot: 'hanhvithenchot',
  anle: 'anle',
  hinhmau: 'lanhdao',
  tieuchigankettrainghiemdiemcham: 'gankettrainghiemdiemcham',
  tieuchitruongthanhvanhoa: 'truongthanhvanhoa',
  actionplan: 'actionplan',
};

const FE_TO_LEGACY_CONTROLLER: Record<string, string> = {
  vanhoa_dashboard: 'dashboard',
  vanhoa_timeline: 'dashboardvanhoa',
  vanhoa_bangkehoach: 'bangkehoach',
  vanhoa_danhgia: 'danhgia',
  vanhoa_ngoinha: 'ngoinha',
  vanhoa_hanhvithenchot: 'hanhvithenchot',
  vanhoa_anle: 'anle',
  vanhoa_cauhoianle: 'cauhoianle',
  vanhoa_dotontap: 'dotontap',
  vanhoa_hinhmau_tieuchi: 'hinhmau',
  vanhoa_hinhmau_luachon: 'hinhmau',
  vanhoa_hinhmau_danhgia: 'hinhmau',
  vanhoa_tieuchigankettrainghiemdiemcham: 'tieuchigankettrainghiemdiemcham',
  vanhoa_tieuchitruongthanhvanhoa: 'tieuchitruongthanhvanhoa',
  vanhoa_dotkhaosat: 'dotkhaosat',
  vanhoa_actionplan: 'actionplan',
  bophan: 'bophan',
  chucvu: 'chucvu',
  nhanvien: 'nhanvien',
  cauhinh: 'cauhinh',
  donvi: 'donvi',
  nhomquyen: 'nhomquyen',
};

const readProp = (obj: unknown, keys: string[]): unknown => {
  if (!obj || typeof obj !== 'object') return undefined;
  const row = obj as Record<string, unknown>;
  for (const k of keys) {
    if (k in row && row[k] !== undefined && row[k] !== null) return row[k];
  }
  return undefined;
};

/** Port `VHWebPartController.MapControllerKey` — server cũng map trước khi filter DB. */
export function mapVhHuongDanControllerKey(strcontroller: string): string {
  let s = (strcontroller || '').trim().toLowerCase();
  if (!s) return '';

  if (['bophan', 'chucvu', 'nhanvien', 'donvi', 'cauhinh', 'nhomquyen'].includes(s)) {
    s = 'thietlap';
  } else if (s === 'hanhvithenchot' || s === 'anle') {
    s = 'thuvien';
  } else if (s === 'danhgia' || s === 'ngoinha') {
    s = 'ngoinhavanhoa';
  }

  return s;
}

export function normalizeVhHuongDanParentId(parentID: string | number | null | undefined): number {
  const n = Number(parentID);
  return Number.isFinite(n) ? n : 0;
}

export function normalizeVhHuongDanItem(raw: unknown): VHHuongDanItem {
  const id = Number(readProp(raw, ['id', 'ID']) ?? 0);
  return {
    id: Number.isFinite(id) ? id : 0,
    parentID: String(readProp(raw, ['parentID', 'ParentID']) ?? '0'),
    ten: (readProp(raw, ['ten', 'Ten']) as string | null | undefined) ?? null,
    noiDung: (readProp(raw, ['noiDung', 'NoiDung']) as string | null | undefined) ?? null,
    viTri: Number(readProp(raw, ['viTri', 'ViTri']) ?? 0),
    controller: (readProp(raw, ['controller', 'Controller']) as string | null | undefined) ?? null,
    target: (readProp(raw, ['target', 'Target']) as string | null | undefined) ?? null,
    placement: (readProp(raw, ['placement', 'Placement']) as string | null | undefined) ?? null,
    arrowOffset: (readProp(raw, ['arrowOffset', 'ArrowOffset']) as string | null | undefined) ?? null,
    xOffset: (readProp(raw, ['xOffset', 'XOffset']) as string | null | undefined) ?? null,
    yOffset: (readProp(raw, ['yOffset', 'YOffset']) as string | null | undefined) ?? null,
    nguoiTao: readProp(raw, ['nguoiTao', 'NguoiTao']) as number | null | undefined,
  };
}

/** Parse body `VHWebPartHuongDanSuDungResponse` từ MS.Culture. */
export function parseVhHuongDanSuDungResponse(raw: unknown): VHHuongDanSuDungResponse {
  const listRaw = readProp(raw, ['listVHHuongDan', 'ListVHHuongDan']);
  const list = Array.isArray(listRaw) ? listRaw.map(normalizeVhHuongDanItem) : [];

  return {
    controllerKey: String(readProp(raw, ['controllerKey', 'ControllerKey']) ?? ''),
    txtyeutothanhcong: (readProp(raw, ['txtyeutothanhcong', 'Txtyeutothanhcong']) as string | null | undefined) ?? null,
    hasVideo: Boolean(readProp(raw, ['hasVideo', 'HasVideo'])),
    hasDemo: Boolean(readProp(raw, ['hasDemo', 'HasDemo'])),
    listVHHuongDan: list,
  };
}

export function unwrapVhCultureApiResponse<T = unknown>(
  response: unknown,
): { success: boolean; message?: string; data?: T } {
  const r = response as Record<string, unknown> | null | undefined;
  const body =
    r && typeof r === 'object' && r.success !== undefined
      ? r
      : r && typeof r.data === 'object' && (r.data as Record<string, unknown>).success !== undefined
        ? (r.data as Record<string, unknown>)
        : r;

  if (body && typeof body === 'object' && body.success !== undefined) {
    return {
      success: Boolean(body.success),
      message: String(body.message ?? (body as { msg?: string }).msg ?? ''),
      data: body.data as T,
    };
  }

  return { success: true, data: (r?.data ?? r) as T };
}

export function isVanHoaPath(pathname: string | undefined | null): boolean {
  const raw = String(pathname || '').split('?')[0].split('#')[0];
  return raw === '/van-hoa' || raw.startsWith('/van-hoa/');
}

export function isVanHoaHuongDanController(controller: string | null | undefined): boolean {
  if (!controller) return false;
  return (VH_HUONG_DAN_FE_CONTROLLERS as readonly string[]).includes(controller);
}

/** Map controller FE → query `strcontroller` (legacy MVC name, server sẽ MapControllerKey). */
export function mapFeControllerToVhHuongDanKey(feController: string, pathname?: string): string {
  if (FE_TO_LEGACY_CONTROLLER[feController]) {
    return FE_TO_LEGACY_CONTROLLER[feController];
  }

  const cleanPath = (pathname || '').replace(/^\//, '');
  if (cleanPath.includes('van-hoa/xuong-ca-ke-hoach')) {
    return 'bangkehoach';
  }

  return feController.replace(/^vanhoa_/, '').toLowerCase();
}

export function resolveVhHuongDanFromPath(pathname: string): {
  feController: string | null;
  strcontroller: string;
  controllerKey: string;
  defaultTarget?: string;
} {
  const feController = getControllerFromRoute(pathname);
  if (!feController) {
    return { feController: null, strcontroller: '', controllerKey: '' };
  }
  const strcontroller = mapFeControllerToVhHuongDanKey(feController, pathname);
  const controllerKey = mapVhHuongDanControllerKey(strcontroller);
  const defaultTarget = VH_HUONG_DAN_DEFAULT_TARGET[strcontroller];
  return { feController, strcontroller, controllerKey, defaultTarget };
}
